/**
 * Unified JSON schema form group
 */
import {
  AbstractType,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  InjectionToken,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Type,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { get, isFunction } from 'lodash';
import { XuxInputComponent } from '@geniuspos/ui-shared/modules/xdm-shared-components/components';

import {
  EInputEvent,
  IArtificialInputWatcherOption,
  XuxFieldPreferences,
} from '@shared/types';
import { Subject } from 'rxjs-compat';

import { DEFAULT_ARTIFICIAL_INPUT_THRESHOLD } from '@config/global-constants';
import { artificialInputWatcher } from '@app/shared/utils/common-utils';
import { lazyInject } from '@app/containers/base/decorators';
import { ScanningService } from '@app/shared/services/scanning/scanning.service';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xux-xvi-input',
  templateUrl: './xux-xvi-input.component.html',
  styleUrls: ['./xux-xvi-input.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Notice: It is not possible to just switch this component to OnPush without breaking a big part of the app
})
export class XuxXVIInputComponent
  extends XuxInputComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @ViewChild('defaultFocusElement', { static: false })
  public defaultFocusElement: ElementRef;
  @Input() public field: XuxFieldPreferences = {};
  @lazyInject(ScanningService) public scanningService: ScanningService;

  public cancelSubscription$ = new Subject<void>();

  public constructor(public injector: Injector) {
    super();
  }

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  protected artificialInputCheck:
    | {
        artificialInputThreshold?: number;
      }
    | boolean = true;

  protected get artificialInputThreshold() {
    return get(
      this.artificialInputCheck,
      'artificialInputThreshold',
      DEFAULT_ARTIFICIAL_INPUT_THRESHOLD,
    );
  }

  public ngOnInit(): void {
    super.ngOnInit();
    this.artificialInputCheck = get(
      this.field,
      'widgetData.artificialInputCheck',
      false,
    );
  }

  public ngAfterViewInit(): void {
    if (this.artificialInputCheck) {
      const artificialInputWatcherOption: IArtificialInputWatcherOption = {
        nativeElement: this.defaultFocusElement.nativeElement,
        cancelSubscription$: this.cancelSubscription$,
        artificialInputThreshold: this.artificialInputThreshold,
      };

      artificialInputWatcher(
        EInputEvent.KEYDOWN,
        {
          ...artificialInputWatcherOption,
          updateValueBeforeEventHandler: true,
          scanningService: this.scanningService,
        },
        this.setNewValue.bind(this),
      );
    }
  }

  public ngOnDestroy(): void {
    this.cancelSubscription$.next();
  }

  public setNewValue(
    event: KeyboardEvent,
    artificialInputWatcherOption: IArtificialInputWatcherOption,
    newValue: string,
  ): void {
    const { updateValueBeforeEventHandler, isValidScanning } =
      artificialInputWatcherOption;

    if (updateValueBeforeEventHandler && !isValidScanning) {
      this.field.control.setValue(newValue);
      this.field.control.markAsDirty();
    }
  }
}
